import type { ChangeEvent } from 'react';
import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import type {
  CollectionItem,
  ImageValue,
  TextStyleValue
} from '@elfsight/configurator-contracts';
import { createReadableClass } from '@elfsight/widget-utils';
import { shuffle } from 'es-toolkit';
import { useRect } from 'react-use-rect';
import type { ConditionValue, LogicValue } from '../../../configurator';
import { useFormDispatch } from '../../contexts/hooks';
import { ImageOption, Text } from './image-option';

const ITEM_GAP = 8;
const MIN_WIDTH = 140;
const MAX_WIDTH = 248;

export interface ImageChoiceProps {
  id?: string;
  label?: string;
  options?: CollectionItem<{
    name: string;
    caption: string;
    image: ImageValue;
    index: number;
  }>[];
  showLabel?: boolean;
  multiple?: boolean;
  required?: boolean;

  fieldLabelPosition?: string;
  fieldLabelColor?: string;
  fieldLabelFontStyle?: TextStyleValue;
  fieldHintColor?: string;
  fieldHintFontStyle?: TextStyleValue;

  fieldBorderType?: string;
  fieldBorderWidth?: number;
  fieldBorderColor?: string;
  fieldBorderRadius?: number;
  fieldFillColor?: string;
  fieldActiveColor?: string;
  fieldActiveFillColor?: string;

  aspectRatio?: number;
  width?: number;
  presetWidth?: number;
  alignment?: 'left' | 'center' | 'right';
  randomize?: boolean;
  hasConditionalLogic?: boolean;
  conditionalLogic?: LogicValue;
  conditions?: ConditionValue[];
  placeholderColor?: string;
  variants?: any;

  onFocus?: () => void;
  onBlur?: () => void;
  onChange?: (
    value: string[],
    event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => void;
  initialValue?: string;
  initialValueMultiple?: string[];
  withInitialValue?: boolean;
  value?: string[];

  valid?: boolean;
  fieldColor?: string;
  fieldFontStyle?: TextStyleValue;
  fieldPlaceholderFontStyle?: string;
  fieldPlaceholderFontWeight?: string;
  fieldSpacing?: number;

  underlineVariants?: Record<string, { borderColor: string }>;
  _spacing?: boolean;
  customVariant?: any;
}

export function ImageChoice({
  id,
  label,
  value,
  options = [],
  randomize = false,
  showLabel,
  multiple,
  required,
  fieldLabelPosition,
  fieldLabelColor,
  fieldLabelFontStyle,
  fieldHintColor,
  fieldHintFontStyle,
  initialValue,
  initialValueMultiple,
  withInitialValue = true,
  ...forwardingProps
}: ImageChoiceProps) {
  const displayLabel = showLabel && fieldLabelPosition === 'inside' && !!label;
  const [containerWidth, setContainerWidth] = useState(0);
  const [containerRef] = useRect(({ width }) => setContainerWidth(width), {
    resize: true
  });
  const dispatch = useFormDispatch();

  const orderIndexes = useMemo(() => {
    const orders = options.map((_, i) => i);
    return randomize ? shuffle(orders) : orders;
    // oxlint-disable-next-line exhaustive-deps
  }, [randomize, options.length]);

  const orderedOptions = useMemo(
    () => orderIndexes.map((i) => options[i]),
    [orderIndexes, options]
  );

  const columnWidth = useMemo(() => {
    const numColumns = Math.min(
      Math.floor((containerWidth + ITEM_GAP) / (MIN_WIDTH + ITEM_GAP)),
      Number(options?.length)
    );
    return Math.min(
      Math.floor(
        (containerWidth - ITEM_GAP * (numColumns - 1)) / (numColumns || 1)
      ),
      MAX_WIDTH
    );
  }, [containerWidth, options]);

  const currentInitialValue = useMemo(
    () => (multiple ? initialValueMultiple : initialValue),
    [multiple, initialValueMultiple, initialValue]
  );

  useEffect(() => {
    if (withInitialValue) {
      dispatch({
        type: 'changeField',
        field: {
          id: String(id),
          value: currentInitialValue,
          valid: true,
          type: 'imageChoice'
        }
      });
    }
    // oxlint-disable-next-line exhaustive-deps
  }, [currentInitialValue, withInitialValue]);

  return (
    <>
      {displayLabel && (
        <Label _textColor={fieldLabelColor} _textStyle={fieldLabelFontStyle}>
          {label}
        </Label>
      )}
      <OptionsContainer
        ref={containerRef}
        role={multiple ? undefined : 'radiogroup'}
        aria-required={multiple ? undefined : required}
      >
        {orderedOptions?.map((option) => (
          <ImageOption
            label={label}
            value={value}
            id={option.id}
            key={option.id}
            option={option}
            itemWidth={columnWidth}
            multiple={multiple}
            required={required}
            fieldHintColor={fieldHintColor}
            fieldFontStyle={fieldLabelFontStyle}
            fieldColor={fieldLabelColor}
            fieldHintFontStyle={fieldHintFontStyle}
            {...forwardingProps}
          />
        ))}
      </OptionsContainer>
    </>
  );
}

const Label = styled(Text).attrs<{
  _textColor?: string;
  _textStyle?: TextStyleValue;
}>({
  className: createReadableClass({
    component: 'fields',
    element: 'image-choice-label'
  })
})`
  display: block;
  margin-bottom: ${ITEM_GAP}px;
`;

const OptionsContainer = styled.div.attrs({
  className: createReadableClass({
    component: 'fields',
    element: 'image-choice-options-container'
  })
})`
  display: flex;
  flex-wrap: wrap;
  gap: ${ITEM_GAP}px;
`;
